'use client';

import { useState } from 'react';
import {
  format,
  addMonths,
  subMonths,
  isSameDay,
  isAfter,
  isBefore,
  startOfMonth,
  endOfMonth,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';

export interface DateRangeType {
  startDate: Date | null;
  endDate: Date | null;
}

interface DatePickerProps {
  value: DateRangeType;
  onChange: (value: DateRangeType) => void;
  disabledDates?: Date[];
}

const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  disabledDates = [],
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const startDate = value.startDate;
  const endDate = value.endDate;

  const isDateDisabled = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Zera horas para comparação de data pura

    const isBeforeToday = date < today;
    const isBooked = disabledDates.some((d) => isSameDay(d, date));

    return isBeforeToday || isBooked;
  };

  const handleDayClick = (day: Date) => {
    if (isDateDisabled(day)) return;

    if (!startDate || (startDate && endDate)) {
      onChange({ startDate: day, endDate: null });
    } else if (startDate && !endDate) {
      if (isBefore(day, startDate)) {
        onChange({ startDate: day, endDate: startDate });
      } else {
        onChange({ startDate, endDate: day });
      }
    }
  };

  const renderDays = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);

    const startDay = monthStart.getDay();
    const days = [];

    // Espaços vazios antes do primeiro dia do mês
    for (let i = 0; i < startDay; i++) {
      days.push(<div key={`empty-${i}`} />);
    }

    // Geração dos dias do mês
    for (let day = 1; day <= monthEnd.getDate(); day++) {
      const date = new Date(
        currentMonth.getFullYear(),
        currentMonth.getMonth(),
        day
      );

      const isDisabled = isDateDisabled(date);
      const isSelected =
        (startDate && isSameDay(date, startDate)) ||
        (endDate && isSameDay(date, endDate)) ||
        (startDate &&
          endDate &&
          isAfter(date, startDate) &&
          isBefore(date, endDate));

      days.push(
        <div
          key={date.toString()}
          onClick={() => handleDayClick(date)}
          className={`flex items-center justify-center cursor-pointer rounded-lg p-2 text-sm
            ${
              isDisabled
                ? 'text-gray-400 cursor-not-allowed line-through'
                : isSelected
                ? 'bg-nat-reggaex text-white'
                : 'hover:bg-gray-200'
            }`}
        >
          {day}
        </div>
      );
    }

    return days;
  };

  return (
    <div className="w-full border border-gray-300 rounded-xl p-4">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <button
          onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
          className="text-sm hover:bg-gray-200 p-1 rounded"
        >
          ◀️
        </button>
        <div className="text-lg font-semibold capitalize">
          {format(currentMonth, 'MMMM yyyy', { locale: ptBR })}
        </div>
        <button
          onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
          className="text-sm hover:bg-gray-200 p-1 rounded"
        >
          ▶️
        </button>
      </div>

      {/* Dias da semana */}
      <div className="grid grid-cols-7 gap-1 text-center mb-2 text-xs text-gray-500">
        {['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SAB'].map((day) => (
          <div key={day}>{day}</div>
        ))}
      </div>

      {/* Grade dos dias */}
      <div className="grid grid-cols-7 gap-1">{renderDays()}</div>

      {/* Footer */}
      <div className="mt-4 text-xs text-gray-500">
        <div>Selecione uma data de início e fim.</div>
      </div>
    </div>
  );
};

export default DatePicker;
